import { useState } from 'react';
import { Button, Card, Modal } from 'react-bootstrap';
import { Ban, Copy, Trash } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ActionItem } from '../../components/ActionItem';
import { Contractor } from '../../domain/contractor';
import { useContractorBlacklistRepository } from '../../repository/contractors/useContractorBlacklistRepository';
import { useRemoveContractorRepository } from '../../repository/contractors/useRemoveContractorRepository';

type ContractorActionsCardProps = {
    contractor: Contractor;
    onCreateNewTransportOrder: () => void;
}

export const ContractorActionsCard = (props: ContractorActionsCardProps) => {
    const { contractor, onCreateNewTransportOrder } = props;
    const navigate = useNavigate();

    const [showModal, setShowModal] = useState(false);

    const { addContractorToBlacklist, removeContractorFromBlacklist } = useContractorBlacklistRepository();
    const { removeContractor, isRemovingContractorPending } = useRemoveContractorRepository();

    const addToBlacklist = async () => {
        await addContractorToBlacklist(contractor.id);
        toast.success('Pomyślnie dodano podwykonawcę do czarnej listy');
    };

    const removeFromBlacklist = async () => {
        await removeContractorFromBlacklist(contractor.id);
        toast.success('Pomyślnie usunięto podwykonawcę z czarnej listy');
    };

    const openRemoveContractorModal = () => {
        setShowModal(true);
    };

    const closeRemoveContractorModal = () => {
        setShowModal(false);
    };

    const onRemoveContractorConfirm = async () => {
        try {
            await removeContractor(contractor.id);
            toast.success('Pomyślnie usunięto podwykonawcę');
            closeRemoveContractorModal();
            navigate('/contractors');
        } catch (error: any) {
            if (error?.error === 'CONTRACTOR_TO_REMOVE_HAS_TRANSPORT_ORDERS') {
                toast.error('Nie można usunąć podwykonawcy, który posiada przypisane zlecenia');
            } else {
                toast.error('Nie udało się usunąć podwykonawcy');
            }
        }
    };

    return (
        <>
            <Card className="mb-auto">
                <Card.Body>
                    <ul className="list-unstyled">
                        <li>
                            <ActionItem onClick={onCreateNewTransportOrder}>
                                <Copy />{' '}Nowe zlecenie
                            </ActionItem>
                        </li>
                        {!contractor.blacklist && <li>
                            <ActionItem onClick={addToBlacklist}>
                                <Ban />{' '}Dodaj do czarnej listy
                            </ActionItem>
                        </li>}
                        {contractor.blacklist && <li>
                            <ActionItem onClick={removeFromBlacklist}>
                                <Ban />{' '}Usuń z czarnej listy
                            </ActionItem>
                        </li>}
                        <li>
                            <ActionItem onClick={openRemoveContractorModal}>
                                <Trash />{' '}Usuń podwykonawcę
                            </ActionItem>
                        </li>
                    </ul>
                </Card.Body>
            </Card>

            <RemoveContractorModalConfirmation
                show={showModal}
                onHide={closeRemoveContractorModal}
                onConfirm={onRemoveContractorConfirm}
                isBusy={isRemovingContractorPending}
            />
        </>
    );
};

type RemoveContractorModalConfirmationProps = {
    show: boolean;
    onHide: () => void;
    onConfirm: () => void;
    isBusy: boolean;
}

const RemoveContractorModalConfirmation = (props: RemoveContractorModalConfirmationProps) => {
    const { show, onHide, onConfirm, isBusy } = props;

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Usuń podwykonawcę</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                Podwykonawca nie może mieć przypisanych żadnych zleceń<br />
                Czy na pewno chcesz usunąć podwykonawcę? Ta operacja jest nieodwracalna.
            </Modal.Body>
            <Modal.Footer>
                <Button disabled={isBusy} variant="secondary" onClick={onHide}>
                    Anuluj
                </Button>
                <Button disabled={isBusy} variant="danger" onClick={onConfirm}>
                    Usuń
                </Button>
            </Modal.Footer>
        </Modal>
    );
};